import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Add, Search, Clear, Close } from "@mui/icons-material";
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import ProductsTable from "../components/ProductsTable";
import ProductFormModal from "../components/ProductFormModal";
import Pagination from "../components/Pagination";
import { useProductsApi } from "../hooks/useProductsApi";
import { Product, PaginationMeta } from "../types/entities";
import { ProductFormData } from "../types/forms";

const ProductsPage: React.FC = () => {
  const { t } = useTranslation();
  const [products, setProducts] = useState<Product[]>([]);
  const [paginationMeta, setPaginationMeta] = useState<PaginationMeta | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [initialLoading, setInitialLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [openCreateModal, setOpenCreateModal] = useState(false);
  const [openEditModal, setOpenEditModal] = useState(false);
  const [openDeleteDialog, setOpenDeleteDialog] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [formData, setFormData] = useState<ProductFormData>({
    name: "",
    amount: 0,
    sell_price: "",
    description: "",
  });

  const {
    error: productsError,
    fetchProducts,
    createProduct: createProductApi,
    updateProduct: updateProductApi,
    deleteProduct: deleteProductApi,
  } = useProductsApi();

  const loadProducts = async (page: number = currentPage, limit: number = itemsPerPage): Promise<void> => {
    try {
      // Ensure page and limit are numbers
      const pageNum = Number(page);
      const limitNum = Number(limit);
      const queryParams: any = { 
        page: pageNum, 
        limit: limitNum
      };
      
      // Add search parameter if search term exists
      if (searchTerm && searchTerm.trim()) {
        queryParams.search = searchTerm.trim();
      }

      const response = await fetchProducts(queryParams);
      setProducts(response.data);
      setPaginationMeta(response.meta);
    } catch (err) {
      console.error('Error loading products:', err);
    }
  };

  const createProduct = async (data: ProductFormData): Promise<void> => {
    try {
      await createProductApi(data);
      toast.success(t('products.productCreated'));
      setOpenCreateModal(false);
      setFormData({ name: "", amount: 0, sell_price: "", description: "" });
      await loadProducts();
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const updateProduct = async (data: ProductFormData): Promise<void> => {
    if (!selectedProduct) return;
    
    try {
      await updateProductApi(selectedProduct.id, data);
      toast.success(t('products.productUpdated'));
      setOpenEditModal(false);
      setSelectedProduct(null);
      setFormData({ name: "", amount: 0, sell_price: "", description: "" });
      await loadProducts();
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const deleteProduct = async (): Promise<void> => {
    if (!selectedProduct) return;
    
    try {
      await deleteProductApi(selectedProduct.id);
      toast.success(t('products.productDeleted'));
      setOpenDeleteDialog(false);
      setSelectedProduct(null);
      await loadProducts();
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleCreateClick = () => {
    setFormData({ name: "", amount: 0, sell_price: "", description: "" });
    setOpenCreateModal(true);
  };

  const handleEditClick = (product: Product) => {
    setSelectedProduct(product);
    setFormData({
      name: product.name,
      amount: product.amount,
      sell_price: product.sell_price || "",
      description: product.description || "",
    });
    setOpenEditModal(true);
  };

  const handleDeleteClick = (product: Product) => {
    setSelectedProduct(product);
    setOpenDeleteDialog(true);
  };

  const handleUpdateAmount = async (product: Product, newAmount: number) => {
    try {
      await updateProductApi(product.id, { amount: newAmount });
      toast.success(t('products.productUpdated'));
      await loadProducts();
    } catch (err) {
      // Error is handled by the hook
    }
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>): void => {
    const value = event.target.value;
    setSearchTerm(value);
    setCurrentPage(1);
  };

  const handleClearSearch = () => {
    setSearchTerm("");
    setCurrentPage(1);
    loadProducts(1, itemsPerPage);
  };

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadProducts(page, itemsPerPage);
  };

  const handleItemsPerPageChange = (items: number) => {
    setItemsPerPage(items);
    setCurrentPage(1);
    loadProducts(1, items);
  };

  // Debounced search effect
  useEffect(() => {
    if (!initialLoading) {
      const timeoutId = setTimeout(() => {
        // Reset to page 1 when searching
        setCurrentPage(1);
        loadProducts(1, itemsPerPage);
      }, 300);

      return () => clearTimeout(timeoutId);
    }
  }, [searchTerm, itemsPerPage, initialLoading]);

  useEffect(() => {
    const initializeProducts = async () => {
      try {
        console.log('Initializing products...');
        await loadProducts();
        console.log('Initial products loaded');
      } finally {
        setInitialLoading(false);
      }
    };

    initializeProducts();
  }, []);

  if (initialLoading) {
    return (
      <Container>
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        sx={{ 
          mb: 3,
          flexDirection: isMobile ? 'column' : 'row',
          gap: isMobile ? 2 : 0,
          textAlign: isMobile ? 'center' : 'left'
        }}
      >
        <Typography variant={isMobile ? "h5" : "h4"} gutterBottom>
          {t('products.title')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={handleCreateClick}
          size={isMobile ? 'medium' : 'large'}
          fullWidth={isMobile}
        >
          {t('products.addProduct')}
        </Button>
      </Box>

      {productsError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {productsError.message}
        </Alert>
      )}

      {/* Always show search input */}
      <Box sx={{ mb: 3 }}>
        <TextField
          fullWidth
          placeholder={t('products.searchPlaceholder')}
          value={searchTerm}
          onChange={handleSearchChange}
          size={isMobile ? 'medium' : 'medium'}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  aria-label="clear search"
                  onClick={handleClearSearch}
                  edge="end"
                  size="small"
                >
                  <Clear />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Box>

      {/* Show results or no results message */}
      {!products || products.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 4 }}>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            {searchTerm ? t('products.noProductsMatching', { searchTerm }) : t('products.noProductsFound')}
          </Typography>
          {!searchTerm && (
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              {t('products.noProductsMessage')}
            </Typography>
          )}
          {searchTerm && (
            <Button
              variant="outlined"
              onClick={handleClearSearch}
              startIcon={<Clear />}
              sx={{ mt: 2 }}
            >
              {t('products.clearSearch')}
            </Button>
          )}
        </Box>
      ) : (
        <>
          <ProductsTable
            products={products}
            onEdit={handleEditClick}
            onDelete={handleDeleteClick}
            onUpdateAmount={handleUpdateAmount}
          />
          {paginationMeta && (
            <Pagination
              meta={paginationMeta}
              onPageChange={handlePageChange}
              onLimitChange={handleItemsPerPageChange}
            />
          )}
        </>
      )}

      <ProductFormModal
        open={openCreateModal}
        onClose={() => setOpenCreateModal(false)}
        onSubmit={createProduct}
        loading={false}
        error={productsError?.message}
      />

      <ProductFormModal
        open={openEditModal}
        onClose={() => {
          setOpenEditModal(false);
          setSelectedProduct(null);
        }}
        onSubmit={updateProduct}
        product={selectedProduct}
        loading={false}
        error={productsError?.message}
      />

      {/* Delete Confirmation Dialog */}
      {selectedProduct && (
        <Box
          sx={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: openDeleteDialog ? 'flex' : 'none',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1300,
          }}
        >
          <Box
            sx={{
              backgroundColor: 'white',
              borderRadius: 2,
              p: 3,
              maxWidth: 400,
              width: '90%',
              textAlign: 'center',
              position: 'relative',
            }}
          >
            <IconButton
              aria-label="close"
              onClick={() => {
                setOpenDeleteDialog(false);
                setSelectedProduct(null);
              }}
              sx={{
                position: 'absolute',
                right: 8,
                top: 8,
                color: (theme) => theme.palette.grey[500],
              }}
            >
              <Close />
            </IconButton>
            <Typography variant="h6" gutterBottom>
              {t('products.deleteProduct')}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {t('products.deleteProductConfirmation', { productName: selectedProduct?.name })}
            </Typography>
            <Typography variant="body2" color="error" sx={{ mb: 3 }}>
              {t('products.deleteProductWarning')}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                color="error"
                onClick={deleteProduct}
              >
                {t('common.delete')}
              </Button>
            </Box>
          </Box>
        </Box>
      )}
    </Container>
  );
};

export default ProductsPage;